import { FormControl, TextLengthCheckOptionType } from "@/models/form-types";
import {
  createAcceptControl, createCheckboxControl, createEmailControl, createIntroControl, createPhoneControl,
  createRadioControl, createTextControl, createTextareaControl, createUrlControl,
} from "@/helpers/utils/utility-functions";
import {
  FILM_EARLY_DEADLINE, FILM_FESTIVAL_DATES, FILM_FINAL_DEADLINE, FILM_INDUSTRY_CLOSES_ON, FILM_LINEUP_DATE,
  FILM_OPENS_ON, FILM_REGULAR_DEADLINE, FILM_SELECTION_DATE,
  filmAgeOptions, filmAiUseOptions, filmAttendInPersonOptions, filmAttendanceOptions, filmBudgetRangeOptions,
  filmCaptionOptions, filmContentAdvisoryOptions, filmDecisionRoleOptions, filmDistributionStatusOptions,
  filmExhibitionFormatOptions, filmFinalCutOptions, filmFinancingSecuredOptions, filmFinancingSourceOptions,
  filmFormatOptions, filmFormatsOfInterestOptions, filmGenreChoices, filmGuardianConsentOptions, filmHeardAboutOptions,
  filmIndustryAgeOptions, filmIndustryTerms, filmLookingForOptions, filmMarketingOptInOptions,
  filmMeetingCapacityOptions, filmMeetingDayOptions, filmMeetingModeOptions, filmMusicClearedOptions,
  filmNetworkTurnoutOptions, filmOrganizationTypeOptions, filmPanelInterestOptions, filmPartnerTypeOptions,
  filmPitchMaterialsConsent, filmPitchRoleOptions, filmPitchTrackOptions, filmPremiereStatusOptions,
  filmPrivacyAcknowledgement, filmProjectFormatOptions, filmProjectStageOptions, filmPublicOnlineOptions,
  filmRightsAvailableOptions, filmRightsHolderOptions, filmRightsLicence, filmSeekingOptions, filmShareChannelOptions,
  filmSoundOptions, filmStyleOptions, filmSubmitterRoleOptions, filmSubtitleOptions, filmTeamIdentityOptions,
  filmTypeOptions, filmYesNoOptions,
} from "@/helpers/utils/film-constants";

export { filmIndustrySchema, filmPitchSchema, filmSubmissionSchema } from "@/helpers/utils/film-schema";

const WORDS = TextLengthCheckOptionType.WORD_COUNT;
const OPTIONAL = "( optional )";
const optionalNote = (note: string) => `${OPTIONAL} ${note}`;
const words = (label: string, max: number, note: string = "", isOptional = false) => {
  const help = [note, `${max} words maximum.`].filter(Boolean).join(" ");
  return createTextareaControl(label, isOptional ? optionalNote(help) : help, isOptional, undefined, max, WORDS);
};
const optionalText = (label: string, note: string = "") => createTextControl(label, note ? optionalNote(note) : OPTIONAL, true);
const optionalUrl = (label: string, note: string = "") => createUrlControl(label, note ? optionalNote(note) : `${OPTIONAL} Include https://`, true);

const timeline = `Applications open ${FILM_OPENS_ON} and close ${FILM_FINAL_DEADLINE}. Early deadline: ${FILM_EARLY_DEADLINE}. Regular deadline: ${FILM_REGULAR_DEADLINE}. Applications in by the early or regular deadline are reviewed first.`;
const decisions = `Selection emails go out by ${FILM_SELECTION_DATE}. The lineup is announced ${FILM_LINEUP_DATE}.`;
const noTravel = "C-Tribe does not cover travel unless stated in a selection letter.";

const contactControls = (phoneOptional = false): Record<string, FormControl> => ({
  firstName: createTextControl("First name"),
  lastName: createTextControl("Last name"),
  email: createEmailControl("Email address"),
  phoneNumber: createPhoneControl("Phone number", phoneOptional ? OPTIONAL : "", phoneOptional),
  city: createTextControl("City and province, state, or region"),
  country: createTextControl("Country"),
});

const ageAndGuardianControls: Record<string, FormControl> = {
  ageConfirmation: createRadioControl("How old are you?", filmAgeOptions),
  guardianName: optionalText("Parent or guardian name", "Required if you are under 18."),
  guardianEmail: createEmailControl("Parent or guardian email", optionalNote("Required if you are under 18."), true),
  guardianConsent: createCheckboxControl("Parent or guardian consent", filmGuardianConsentOptions, optionalNote("To be ticked by the parent or guardian if the applicant is under 18. We cannot accept an under-18 application without it."), true),
};

const heardAboutControls = (isOptional = false): Record<string, FormControl> => ({
  heardAbout: createRadioControl("How did you hear about C-Tribe x Film?", filmHeardAboutOptions, isOptional ? OPTIONAL : "", isOptional),
  heardAboutOther: optionalText("If other, where did you hear about us?"),
});

const turnoutHelp = "A rough guess is fine. Friends, family, cast, crew and community all count. It helps us choose venue sizes and screening times.";
const audienceControls = (turnoutLabel: string): Record<string, FormControl> => ({
  networkTurnout: createRadioControl(turnoutLabel, filmNetworkTurnoutOptions, turnoutHelp),
  shareChannels: createCheckboxControl("If you are selected, how would you let people know?", filmShareChannelOptions, "Optional. We share a promo kit with every selected filmmaker.", true),
});

const privacyAcknowledgement = createAcceptControl(filmPrivacyAcknowledgement);
const marketingOptIn = createCheckboxControl("Stay in touch", filmMarketingOptInOptions, optionalNote("Leave this unticked if you only want emails about your application."), true);

export const filmSubmissionControls: Record<string, FormControl> = {
  intro: createIntroControl("C-Tribe x Film 2027: Submit your film", [
    `C-Tribe x Film runs six days of screenings across Edmonton venues, ${FILM_FESTIVAL_DATES}. Features, documentaries, shorts, animation, experimental work and music videos. After the credits roll, the filmmaker is in the room.`,
    "We prioritise work from first-time directors, Indigenous storytellers, diaspora voices and artists working outside the mainstream pipeline. There is no entry fee.",
    "Films completed on or after January 1, 2025 are eligible: shorts, features, episodic work and music videos. Premieres are preferred for opening and closing night but are not required. The full film must not be publicly available online.",
    `${timeline} ${decisions}`,
    "Have your screener link ready, plus a synopsis, a director bio and a link to one production still. It takes about 20 minutes.",
    "Looking for a meeting with distributors or producers instead? Apply for a pitch meeting at clabs.ctribefestival.com/form/film/pitch.",
  ]),

  // About you
  ...contactControls(),
  pronouns: optionalText("Pronouns"),
  submitterRole: createRadioControl("What is your role on this film?", filmSubmitterRoleOptions),
  submitterRoleOther: optionalText("If other, what is your role?"),
  ...ageAndGuardianControls,

  // The film
  filmTitle: createTextControl("Film title", "Use the title as it appears on screen."),
  filmTitleEnglish: optionalText("English title, if different"),
  filmFormat: createRadioControl("What kind of work is it?", filmFormatOptions, "Runtime includes credits."),
  filmType: createRadioControl("Fiction or non-fiction?", filmTypeOptions),
  filmStyle: createCheckboxControl("Does any of these describe it?", filmStyleOptions, optionalNote("Select all that apply."), true),
  filmGenres: createCheckboxControl("Genre", filmGenreChoices, "Pick up to three."),
  runtimeMinutes: createTextControl("Runtime in minutes", "Whole minutes, credits included. For a series, give the total of the episodes you are submitting."),
  episodeCount: optionalText("Number of episodes submitted", "Series only."),
  completionMonth: createTextControl("Completion date (month and year)", "Use MM/YYYY. Films completed on or after January 1, 2025 are eligible. Works in progress are fine if the cut is near final; tell us the expected lock date at the end of the form."),
  isWorkInProgress: createRadioControl("Is this the final cut?", filmFinalCutOptions, "We review the cut you send. We cannot rewatch later versions."),
  countryOfProduction: createTextControl("Country or countries of production"),
  productionProvince: optionalText("If made in Canada, which province or territory?"),
  isAlbertaFilm: createRadioControl("Was this film made in Alberta, or is the director based in Alberta?", filmYesNoOptions),
  languages: createTextControl("Original language(s)"),
  hasEnglishSubtitles: createRadioControl("Does the film have English subtitles, if any dialogue is not in English?", filmSubtitleOptions, "Selected films with non-English dialogue need English subtitles burned in or as a file."),

  // Screener
  screenerUrl: createUrlControl("Private screener link", "Vimeo, YouTube (unlisted) or similar streaming link. Download-only links are not accepted. Keep it live until August 31, 2027."),
  screenerPassword: optionalText("Screener password", "Leave blank if the link has no password. Please do not change it while we review."),
  trailerUrl: optionalUrl("Trailer link"),

  // Premiere and distribution status
  premiereStatus: createRadioControl("Where has this film been shown publicly so far?", filmPremiereStatusOptions, "Festivals, public screenings and online releases all count. If you plan to submit to other Edmonton festivals, check their premiere rules before accepting a C-Tribe screening."),
  previousScreenings: words("List festivals or public screenings so far", 150, "Name, city, date.", true),
  isPubliclyOnline: createRadioControl("Is the full film publicly available online now?", filmPublicOnlineOptions),
  upcomingFestivals: words("Any festivals you have submitted to or been selected for in 2027?", 100, "Helps us avoid premiere conflicts.", true),
  distributionStatus: createRadioControl("Does the film have a distributor or sales agent?", filmDistributionStatusOptions),
  distributorName: optionalText("Distributor or sales agent name"),

  // Story and team
  logline: words("Logline", 40, "One or two sentences."),
  shortSynopsis: words("Short synopsis", 75, "We may use this in the festival guide."),
  longSynopsis: words("Long synopsis", 300),
  directorStatement: words("Director's statement", 250, "Why this story, and why you."),
  directorName: createTextControl("Director name(s)"),
  directorBio: words("Director bio", 150, "Written in the third person."),
  keyCrew: words("Key credits (producer, writer, cinematographer, editor, composer)", 150, "", true),
  teamIdentity: createCheckboxControl("Does anyone on the key creative team identify as any of these?", filmTeamIdentityOptions, "Optional. Used for programming priorities and aggregate reporting only, never published.", true),
  filmmakerAttendance: createRadioControl("If selected, could a filmmaker attend in person for the Q&A?", filmAttendanceOptions, `Screenings run ${FILM_FESTIVAL_DATES}. ${noTravel}`),
  aiUse: createRadioControl("Did you use generative AI for any image, sound, voice or script in the film?", filmAiUseOptions),
  aiUseDetail: words("If yes, how?", 100, "", true),

  // Technical and accessibility
  exhibitionFormats: createCheckboxControl("Which screening files can you deliver?", filmExhibitionFormatOptions, "Select all that apply."),
  aspectRatio: optionalText("Aspect ratio", "For example 1.85:1, 2.39:1, 16:9."),
  soundFormat: createRadioControl("Sound", filmSoundOptions, OPTIONAL, true),
  captions: createRadioControl("Do you have a closed caption or SDH file?", filmCaptionOptions, "We want every screening to be accessible."),
  describedVideo: createRadioControl("Do you have a described video (audio description) track?", filmYesNoOptions, OPTIONAL, true),
  contentAdvisories: createCheckboxControl("Content advisories", filmContentAdvisoryOptions, optionalNote("Helps us place the film and warn audiences."), true),

  // Images for promotion (links only for now)
  still1: createUrlControl("Link to a production still (Google Drive, Dropbox, or similar)", "Make sure anyone with the link can view it. Horizontal (wider than tall), at least 1920 px wide, no text or logos."),
  still2: optionalUrl("Link to a second still (Google Drive, Dropbox, or similar)"),
  directorPhoto: optionalUrl("Link to a director headshot (Google Drive, Dropbox, or similar)"),
  pressKitUrl: optionalUrl("Press kit or EPK link"),
  websiteOrSocial: optionalUrl("Film website or social link"),

  // Rights
  rightsHolder: createRadioControl("Are you the rights holder, or authorised by the rights holder to submit this film?", filmRightsHolderOptions),
  musicCleared: createRadioControl("Is all music in the film cleared for festival screenings?", filmMusicClearedOptions),
  musicDetail: words("If in progress, what is outstanding?", 100, "", true),

  // Your audience
  ...audienceControls("If we screen your film, roughly how many people from your circle do you think would come out to see it?"),

  // Final details
  ...heardAboutControls(),
  additionalInfo: words("Anything else we should know?", 150, "For a work in progress, tell us the expected picture lock date.", true),
  privacyAcknowledgement,
  rightsLicence: createAcceptControl(filmRightsLicence),
  marketingOptIn,
};

export const filmPitchControls: Record<string, FormControl> = {
  intro: createIntroControl("C-Tribe x Film 2027: Apply for pitch meetings", [
    `Selected filmmakers get one-on-one meetings with distributors, sales agents, producers and funders during C-Tribe x Film, ${FILM_FESTIVAL_DATES}, in Edmonton. There is no entry fee.`,
    "Two tracks. Finished film: you have a completed film and want distribution or sales. Project: you have a film or series in development, production or post-production and want partners or financing.",
    "Meetings are requested both ways. Industry participants see a short profile of your project and accept the meetings that fit what they are looking for. We cannot promise a set number of meetings.",
    `${timeline} ${decisions}`,
    "Share your pitch deck and any screener as links. We do not accept uploads for decks or screeners.",
    "Want your finished film screened at the festival? Submit it for screening too at clabs.ctribefestival.com/form/film/submission.",
  ]),

  // About you
  ...contactControls(),
  pronouns: optionalText("Pronouns"),
  ...ageAndGuardianControls,
  pitchRole: createRadioControl("Your role on the project", filmPitchRoleOptions),
  companyName: optionalText("Production company (if any)"),
  websiteOrSocial: optionalUrl("Website, IMDb or social link"),
  applicantBio: words("Your bio and key credits", 150),
  pitchTrack: createRadioControl("Which track are you applying for?", filmPitchTrackOptions, "The next questions depend on your answer."),

  // The project (both tracks)
  projectTitle: createTextControl("Title (working title is fine)"),
  projectFormat: createRadioControl("Format", filmProjectFormatOptions),
  projectGenres: createCheckboxControl("Genre", filmGenreChoices, "Pick up to three."),
  logline: words("Logline", 40, "One or two sentences."),
  synopsis: words("Synopsis", 350),
  whyNow: words("Why this story, and why you?", 200),
  targetAudience: words("Who is the audience, and how will you reach them?", 150),
  comparables: optionalText("Two or three comparable titles"),
  languages: createTextControl("Language(s)"),
  countryOfProduction: createTextControl("Country or countries of production"),
  teamBios: words("Key team and attachments", 250, "Producer, director, writer, any cast or partners attached."),
  teamIdentity: createCheckboxControl("Does anyone on the key creative team identify as any of these?", filmTeamIdentityOptions, "Optional. Used for programming priorities and aggregate reporting only, never published.", true),

  // Finished film track
  runtimeMinutes: createTextControl("Runtime in minutes", "Whole minutes, credits included."),
  completionMonth: createTextControl("Completion date (MM/YYYY)", "Films completed on or after January 1, 2025 are eligible."),
  screenerUrl: createUrlControl("Screener link", "Private streaming link (Vimeo, YouTube unlisted or similar). Download-only links are not accepted."),
  screenerPassword: optionalText("Screener password", "Leave blank if the link has no password."),
  premiereStatus: createRadioControl("Where has this film been shown publicly so far?", filmPremiereStatusOptions, "Festivals, public screenings and online releases all count."),
  rightsAvailable: createCheckboxControl("Which rights are available?", filmRightsAvailableOptions, "Select all that apply."),
  salesStatus: words("Distribution or sales so far", 150, "Deals closed, territories sold, offers on the table.", true),

  // Project track
  projectStage: createRadioControl("Stage", filmProjectStageOptions),
  budgetRange: createRadioControl("Total budget (CAD)", filmBudgetRangeOptions),
  financingSecured: createRadioControl("How much of the budget is confirmed?", filmFinancingSecuredOptions, "Confirmed means a signed letter or contract, a grant decision, or money in hand."),
  financingSources: createCheckboxControl("Confirmed or applied-for sources", filmFinancingSourceOptions, optionalNote("Select all that apply."), true),
  financePlan: words("Short financing plan", 200, "Sources, amounts if you can share them, and what is still needed.", true),
  statusReport: words("Where is the project now?", 200),
  projectedCompletion: optionalText("Expected completion (MM/YYYY)"),

  // What you want from meetings
  seeking: createCheckboxControl("What are you looking for?", filmSeekingOptions, "Select all that apply."),
  meetingGoals: words("What would a successful meeting look like for you?", 100),
  partnerTypes: createCheckboxControl("Who do you most want to meet?", filmPartnerTypeOptions, "Select all that apply."),

  // Materials (links only)
  pitchDeckUrl: createUrlControl("Pitch deck link", "PDF on Google Drive, Dropbox or DocSend. Shared only with industry participants you are matched with."),
  sizzleUrl: optionalUrl("Sizzle, teaser or clips link", "10 minutes maximum, one streamable link."),
  sizzlePassword: optionalText("Sizzle or clips password"),
  attendInPerson: createRadioControl(`Can you attend in person in Edmonton, ${FILM_FESTIVAL_DATES}?`, filmAttendInPersonOptions, noTravel),
  meetingDays: createCheckboxControl("Which days could you take meetings?", filmMeetingDayOptions, optionalNote("Select all that apply."), true),

  // Your audience
  ...audienceControls("If you are selected, roughly how many people from your circle do you think would come to C-Tribe x Film to support you?"),

  // Final details
  ...heardAboutControls(),
  additionalInfo: words("Anything else we should know?", 150, "", true),
  privacyAcknowledgement,
  pitchMaterialsConsent: createAcceptControl(filmPitchMaterialsConsent),
  marketingOptIn,
};

export const filmIndustryControls: Record<string, FormControl> = {
  intro: createIntroControl("C-Tribe x Film 2027: Industry registration", [
    `For distributors, sales agents, producers, broadcasters, streamers, funders, agents and programmers who want to meet filmmakers at C-Tribe x Film, ${FILM_FESTIVAL_DATES}, Edmonton.`,
    "Tell us what you are looking for. We will share profiles of selected films and projects that fit, and you choose which meeting requests to accept.",
    "Registration is free. Festival passes are sold separately. We review each registration before sharing filmmaker profiles.",
    `Registration opens ${FILM_OPENS_ON} and closes ${FILM_INDUSTRY_CLOSES_ON}.`,
    "Press and media? Use the media registration form instead at clabs.ctribefestival.com/form/media-press.",
  ]),
  firstName: createTextControl("First name"),
  lastName: createTextControl("Last name"),
  email: createEmailControl("Email address"),
  phoneNumber: createPhoneControl("Phone number", OPTIONAL, true),
  jobTitle: createTextControl("Job title"),
  organizationName: createTextControl("Organisation"),
  organizationWebsite: createUrlControl("Organisation website", "Include https://"),
  linkedinUrl: optionalUrl("LinkedIn or IMDbPro link"),
  city: createTextControl("City and province, state, or region"),
  country: createTextControl("Country"),
  organizationType: createRadioControl("Which best describes your organisation?", filmOrganizationTypeOptions),
  organizationTypeOther: optionalText("If other, please specify"),
  decisionRole: createRadioControl("Can you greenlight, acquire, invest, commission or represent?", filmDecisionRoleOptions),
  lookingFor: createCheckboxControl("What are you looking for?", filmLookingForOptions, "Select all that apply."),
  formatsOfInterest: createCheckboxControl("Formats", filmFormatsOfInterestOptions, "Select all that apply."),
  genresOfInterest: createCheckboxControl("Genres", filmGenreChoices, optionalNote("Select all that apply."), true),
  territories: createTextControl("Territories you work in"),
  budgetBands: createCheckboxControl("Budget ranges you work with (CAD)", filmBudgetRangeOptions, optionalNote("Select all that apply."), true),
  mandate: words("Your current mandate, in a few lines", 150, "Shown to selected filmmakers so they request the right meetings."),
  meetingCapacity: createRadioControl("How many 20-minute meetings could you take?", filmMeetingCapacityOptions),
  meetingDays: createCheckboxControl("Which days are you available?", filmMeetingDayOptions, "Select all that apply."),
  meetingMode: createRadioControl("Meeting format", filmMeetingModeOptions),
  panelInterest: createRadioControl("Would you consider speaking on a panel or leading a workshop?", filmPanelInterestOptions, OPTIONAL, true),
  ageConfirmation: createRadioControl("Are you 18 or older?", filmIndustryAgeOptions, "Industry registration is for adults only."),
  ...heardAboutControls(true),
  privacyAcknowledgement,
  industryTerms: createAcceptControl(filmIndustryTerms),
  marketingOptIn,
};
